import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardCopy,
  Loader2,
  Lock,
  Play,
  RefreshCw,
  Sparkles,
  TriangleAlert,
} from 'lucide-react';
import { Role, hasRole } from '@nexa/auth';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useSession } from '@/auth/use-session';
import { cn } from '@/lib/cn';
import { useQueryClient } from '@tanstack/react-query';
import {
  useDisruptionProgress,
  useIssueDisruptionTickets,
  useResolveNext,
  useSendDisruptionOffers,
  useTransferBoard,
  useTransferRunAction,
} from '@/lib/disruptions/use-disruptions';
import type {
  DisruptionDetail,
  ResolutionOp,
  ResolutionState,
  ResolveNextResult,
} from '@/lib/disruptions/types';
import { formatPriceMinor } from '../labels';
import { WhyButton } from '@/components/why';
import { RebookingProgressDialog } from '../rebooking-progress-dialog';
import { RebookingPolicyNotice } from '../rebooking-policy-notice';
import { AutomaticAcceptancePanel } from '../automatic-acceptance-panel';
import { rebookingPolicyGate } from '../resolution-steps';
import {
  COMMIT_OPS,
  buildAttention,
  buildInProgress,
  buildNow,
  elapsedLabel,
  liveFacts,
  shortDuration,
  type AttentionRow,
  type NowCard,
  type TowerTarget,
} from './tower-model';
import {
  scheduleCatchUp,
  useJournalFeed,
  useRunSamples,
  useTowerJournal,
  type JournalLine,
} from './tower-journal';
import { RebookAgain } from './tower-rebook-again';
import type { TowerT } from './tower-i18n';
import { buildShiftSummary } from './tower-summary';

// THE CONSOLE — pinned on the right in every Tower view. Answers, always in the
// same place: NOW (what do I do now — the ONE primary button, with what will
// happen written before you press it), IN PROGRESS (what is happening — the live
// run), ATTENTION (what needs me — queues with a verb), LOG (what happened).
// Same seams as the classic hero: `resolve-next@v1` fires the recommended op
// (reversible ops on one click; COMMIT ops through the one confirm dialog, with
// `expectedOp` so a state that moved on can never fire the wrong op).

interface TowerConsoleProps {
  disruptionUrn: string;
  detail: DisruptionDetail;
  state: ResolutionState;
  onTarget: (target: TowerTarget) => void;
  className?: string;
}

const NO_LINES: JournalLine[] = [];

export function TowerConsole({
  disruptionUrn,
  detail,
  state,
  onTarget,
  className,
}: TowerConsoleProps) {
  const { t } = useTranslation('tower');
  // `isLoading` / `isError` / `refetch` are read for TAKE THE RECOMMENDATION's
  // loading + error states: the untaken-recommendation COUNT is folded out of
  // this snapshot by `liveFacts`, so an unresolved (or failed) seed read means we
  // do not know it yet — and an accept button over an unknown count is a lie.
  // Still ONE read, still WS-fed thereafter (staleTime Infinity, no interval).
  const {
    data: live,
    isLoading: liveLoading,
    isError: liveError,
    refetch: refetchLive,
  } = useDisruptionProgress(disruptionUrn);
  const resolve = useResolveNext(disruptionUrn);
  // Supervisor REPAIR for a stale automatic commit (accepted, ticket/hotel not
  // issued): re-issues for the accepted groups only — never a case-wide fan-out.
  const repair = useIssueDisruptionTickets(disruptionUrn);
  // Supervisor REPAIR for offers stuck at "ready, not sent" (QA fix 4): re-opens
  // the acceptance gate for the complete offers a missed event left behind.
  const sendOffersRepair = useSendDisruptionOffers(disruptionUrn);
  // MASS ground transport: "Re-dispatch" for the no-shows — one replace-no-shows
  // per run that left passengers at the pickup (the board lists the runs; the
  // Hotels view has the per-hotel / per-run controls).
  const { data: transferBoard } = useTransferBoard(disruptionUrn);
  const runAction = useTransferRunAction(disruptionUrn);
  const qc = useQueryClient();
  const { role } = useSession();
  const [confirmOp, setConfirmOp] = useState<ResolutionOp | null>(null);
  const [dialogOp, setDialogOp] = useState<'rebooking' | 'allocation' | null>(null);
  const [fired, setFired] = useState<ResolveNextResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [checking, setChecking] = useState(false);
  const facts = liveFacts(state, live);
  // Pre-run rebooking policy gate — the same read Overview, Passengers and the
  // express board make (rebooking TDS §18).
  const towerPolicyGate = rebookingPolicyGate(state.steps);
  const [now, setNow] = useState(() => Date.now());
  const append = useTowerJournal((s) => s.append);
  const setPending = useTowerJournal((s) => s.setPending);
  const pending = useTowerJournal((s) => s.pending[disruptionUrn]) ?? null;
  // Selector must return a STABLE snapshot (useSyncExternalStore): default outside.
  const lines = useTowerJournal((s) => s.lines[disruptionUrn]) ?? NO_LINES;
  useJournalFeed(disruptionUrn, live, state, t);
  const samples = useRunSamples(disruptionUrn, live);

  // The ONE clock of the page (elapsed / ETA / stall) — a wall clock, not a poll.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // airline-money@v2 — the card's amounts are PRICES; null → «No disponible».
  const card = useMemo(
    () => buildNow(state, live, (minor, currency) => formatPriceMinor(minor, currency, t), t),
    [state, live, t],
  );
  const run = useMemo(
    () => buildInProgress(state, live, samples, now, pending, t),
    [state, live, samples, now, pending, t],
  );
  const attention = useMemo(() => buildAttention(state, live, t), [state, live, t]);

  useEffect(() => {
    if (fired && fired.startedOp !== 'none' && state.nextStep.op !== fired.startedOp)
      setFired(null);
  }, [fired, state.nextStep.op]);
  // The pending marker hands over as soon as a run marker shows up (the run is
  // the truth from then on) and can never outlive its usefulness (90 s guard).
  useEffect(() => {
    if (!pending) return;
    if (live?.activeRun || now - pending.since > 90_000) setPending(disruptionUrn, null);
  }, [pending, live?.activeRun, now, disruptionUrn, setPending]);

  function fire(op: ResolutionOp): void {
    // The picture reacts from the CLICK, not from the first WS push: the fire is
    // marked pending (IN PROGRESS + the river's dash read it) and logged now; the
    // synchronous fan-outs (tickets / vouchers / acceptance) only answer when the
    // airline did, and durable runs take a push or two to show a marker.
    const step = state.steps.find((s) => s.op === op);
    const count = step?.remaining ?? step?.total ?? 0;
    setPending(disruptionUrn, { op, since: Date.now(), count });
    append(
      disruptionUrn,
      t('console.fired', {
        action: op === card.op ? card.cta : t('console.retryOp', { op }),
      }),
      'info',
    );
    resolve.mutate(
      { dryRun: false, expectedOp: op },
      {
        onSuccess: (res) => {
          setConfirmOp(null);
          setFired(res);
          if (res.startedOp === 'none')
            append(disruptionUrn, t('console.nothingRan', { reason: res.reason }), 'warn');
          // No modal on fire: IN PROGRESS + the river ARE the live view; the per-PNR
          // feed stays one click away (“Watch live”).
        },
        onError: (err) =>
          append(disruptionUrn, t('console.couldNotRun', { message: err.message }), 'danger'),
        onSettled: () => {
          setPending(disruptionUrn, null);
          scheduleCatchUp(qc, disruptionUrn);
        },
      },
    );
  }

  function repairIssue(n: number): void {
    if (repair.isPending) return;
    append(disruptionUrn, t('console.repair.sent', { count: n }), 'info');
    repair.mutate(undefined, {
      onSuccess: (res) => {
        const tally = Object.entries(res.tally ?? {})
          .map(([k, v]) => `${k} ${v}`)
          .join(' · ');
        append(
          disruptionUrn,
          tally ? t('console.repair.doneTally', { tally }) : t('console.repair.done'),
          'ok',
        );
      },
      onError: (err) =>
        append(disruptionUrn, t('console.repair.failed', { message: err.message }), 'danger'),
      onSettled: () => scheduleCatchUp(qc, disruptionUrn),
    });
  }

  function sendOffers(n: number): void {
    if (sendOffersRepair.isPending) return;
    append(disruptionUrn, t('console.sendOffers.sent', { count: n }), 'info');
    sendOffersRepair.mutate(undefined, {
      onSuccess: (res) => {
        const tally = Object.entries(res.tally ?? {})
          .map(([k, v]) => `${k} ${v}`)
          .join(' · ');
        append(
          disruptionUrn,
          tally ? t('console.sendOffers.doneTally', { tally }) : t('console.sendOffers.done'),
          'ok',
        );
      },
      onError: (err) =>
        append(disruptionUrn, t('console.sendOffers.failed', { message: err.message }), 'danger'),
      onSettled: () => scheduleCatchUp(qc, disruptionUrn),
    });
  }

  function redispatchNoShows(n: number): void {
    if (runAction.isPending) return;
    const runs = (transferBoard?.runs ?? []).filter(
      (r) => r.noShowCount > 0 && (r.status === 'DEPARTED' || r.status === 'ARRIVED'),
    );
    if (runs.length === 0) {
      // No run to re-cut from here (older BFF / board not loaded): open the shuttle board.
      onTarget({ view: 'hotels', transfer: 'NO_SHOW' });
      return;
    }
    append(disruptionUrn, t('console.redispatch.sent', { count: n, runs: runs.length }), 'info');
    void Promise.allSettled(
      runs.map((r) =>
        runAction.mutateAsync({ runUrn: r.urn, action: 'replace-no-shows', reason: 'Tower · re-dispatch no-shows' }),
      ),
    ).then((results) => {
      const failed = results.filter((x) => x.status === 'rejected').length;
      append(
        disruptionUrn,
        failed
          ? t('console.redispatch.partial', { done: runs.length - failed, failed })
          : t('console.redispatch.ok'),
        failed ? 'warn' : 'ok',
      );
      scheduleCatchUp(qc, disruptionUrn);
    });
  }

  function onPrimary(): void {
    if (card.op === 'none') {
      void copySummary();
      return;
    }
    if (!card.actionable) return;
    // QA fix 4 — the "offers ready, not sent" NOW card fires the send-offers
    // REPAIR (re-open the gate), never resolve-next (which would force-accept).
    if (card.action === 'send-offers') {
      const n = state.steps.find((s) => s.op === 'acceptance')?.remaining ?? 0;
      sendOffers(n);
      return;
    }
    if (COMMIT_OPS.has(card.op)) {
      setConfirmOp(card.op);
      return;
    }
    fire(card.op);
  }

  async function checkDepartures(): Promise<void> {
    setChecking(true);
    const before = facts?.travelled ?? 0;
    try {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['disruptions', disruptionUrn, 'resolution-state'] }),
        qc.invalidateQueries({ queryKey: ['disruptions', disruptionUrn, 'progress'] }),
        qc.invalidateQueries({ queryKey: ['disruptions', disruptionUrn, 'roster'] }),
        qc.invalidateQueries({ queryKey: ['disruptions', disruptionUrn, 'detail'] }),
      ]);
      const fresh = qc.getQueryData<ResolutionState>(['disruptions', disruptionUrn, 'resolution-state']);
      const g2 = fresh?.kpis.groups;
      const after = g2?.travelled ?? before;
      append(
        disruptionUrn,
        t('console.departures.checked', {
          after,
          delta: after > before ? ` (+${after - before})` : '',
          waiting: g2?.ticketedNotDeparted ?? 0,
        }),
        after > before ? 'ok' : 'info',
      );
    } finally {
      setChecking(false);
    }
  }

  async function copySummary(): Promise<void> {
    try {
      await navigator.clipboard?.writeText(buildShiftSummary(detail, state, live, lines, t));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }

  const supervisorBlocked = card.requiresSupervisor && !hasRole(role, Role.OPS_SUPERVISOR);
  const primaryDisabled =
    card.op !== 'none' && (!card.actionable || resolve.isPending || supervisorBlocked);

  return (
    <aside
      className={cn('flex min-h-0 min-w-0 flex-col overflow-hidden bg-background', className)}
      data-testid="tower-console"
      aria-label={t('console.aria')}
    >
      {/* NOW */}
      <section
        className="flex shrink-0 flex-col gap-2 border-b border-border px-4 pb-3.5 pt-3"
        data-testid="tower-now"
      >
        <PanelHeader
          title={t('console.now.title')}
          badge={
            card.mode === 'running'
              ? t('console.badge.running')
              : card.mode === 'wait'
                ? t('console.badge.waiting')
                : card.mode === 'done'
                  ? t('console.badge.closed')
                  : card.mode === 'retry'
                    ? t('console.badge.retry')
                    : t('console.badge.next')
          }
          tone={
            card.mode === 'done'
              ? 'ok'
              : card.mode === 'wait' || card.mode === 'running'
                ? 'info'
                : 'warn'
          }
        />
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {card.eyebrow}
        </p>
        <h2
          className="text-[17px] font-bold leading-tight tracking-tight text-foreground"
          data-testid="tower-now-headline"
        >
          {card.headline}
        </h2>
        {card.why ? <p className="text-xs leading-snug text-muted-foreground">{card.why}</p> : null}
        {card.facts.length ? (
          <dl className="grid gap-1 text-xs">
            {card.facts.map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-between gap-3">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="font-semibold tabular-nums text-foreground">{v}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        <p
          className={cn(
            'rounded-md px-2 py-1.5 text-[11.5px] font-medium',
            card.reversibility === 'reversible' &&
              'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
            card.reversibility === 'irreversible' &&
              'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
            card.reversibility === 'destructive' &&
              'bg-rose-50 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300',
            (card.reversibility === 'wait' || card.reversibility === 'done') &&
              'bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-300',
          )}
          data-testid="tower-now-reversibility"
        >
          {card.reversibilityLine}
        </p>
        <div className="flex min-w-0 items-center gap-2">
          <Button
            size="lg"
            className={cn(
              'h-11 min-w-0 flex-1 gap-2 text-sm',
              card.mode === 'retry' && 'bg-amber-600 hover:bg-amber-700',
              card.reversibility === 'destructive' && 'bg-rose-700 hover:bg-rose-800',
            )}
            variant={card.op === 'none' || card.mode === 'wait' ? 'outline' : 'default'}
            disabled={primaryDisabled}
            onClick={onPrimary}
            title={!card.actionable && card.why ? card.why : undefined}
            data-testid="tower-now-primary"
          >
            {resolve.isPending || card.mode === 'running' ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : card.op === 'none' ? (
              copied ? (
                <Check className="h-4 w-4" aria-hidden />
              ) : (
                <ClipboardCopy className="h-4 w-4" aria-hidden />
              )
            ) : card.mode === 'blocked' || card.mode === 'unknown' || supervisorBlocked ? (
              <Lock className="h-4 w-4" aria-hidden />
            ) : (
              <Play className="h-4 w-4" aria-hidden />
            )}
            <span className="min-w-0 truncate">
              {card.op === 'none' && copied ? t('console.copied') : card.cta}
            </span>
          </Button>
        </div>
        {card.secondary ? (
          <button
            type="button"
            className="inline-flex items-center gap-0.5 self-start text-[11.5px] font-semibold text-primary hover:underline"
            onClick={() => card.secondary && onTarget(card.secondary.target)}
            data-testid="tower-now-secondary"
          >
            {card.secondary.label} <ChevronRight className="h-3 w-3" aria-hidden />
          </button>
        ) : null}
        {/* The RETROSPECTIVE counterpart to `card.why` above (feat/disruption-why
            W6). That line says, in one sentence, why the NEXT step is what it is;
            this opens the disruption's own account of what already happened —
            written as each consequence landed, with the evidence under it. Same
            weight and styling as the secondary link, because it is the same kind
            of affordance: a way out of this card, not an action on it. */}
        <WhyButton
          scope={{ kind: 'disruption', disruptionUrn }}
          testId="tower-why-open"
        />
        {/* Pre-run policy gate (rebooking TDS §18): the station's governing policy
            carries no rebooking block, so no rebook action exists here — say WHY and
            link the rule, in the same words every other surface uses. */}
        {towerPolicyGate.disabled ? (
          <RebookingPolicyNotice
            policyUrn={towerPolicyGate.policyUrn}
            data-testid="tower-rebooking-policy-gate"
          />
        ) : null}
        {/* ── TAKE THE RECOMMENDATION (auto-accept-rebooking / U2, NEX-535) ──
            The Tower mounts the roster `compact`, which drops the action sidebar
            (roster-tab.tsx) — and with it the only surface that could accept
            ALREADY-GENERATED options without a fresh vendor search. The classic
            view could; the Tower, the default view, could not. Observed twice in
            one day: IB376/BCN left 95 groups and WN158/DEN 70 groups sitting at
            OPTIONS_READY, each with a computed "recommended #1" and no way to
            take it. A regression in REACH, not a missing capability.

            DESIGN — option (a) of the brief, chosen deliberately: REUSE
            `AutomaticAcceptancePanel`, the very component the classic sidebar
            mounts, driving the same `auto-rebook@v2` seam with the same args and
            the same `disruptions:autoAccept.*` copy. Explicitly NOT a new
            Tower-only trigger: an operator already has to hold two meanings apart
            — "Generate options" (acceptTopRank:false) and "Rebook again"
            (acceptTopRank:true + regenerate:true) — and a third one with its own
            semantics is precisely what produced this ticket. Nothing new is
            learned here; it is the panel from the other view, in the view they
            actually work in.

            ACCEPT ≠ REGENERATE. The panel posts { acceptTopRank, qualityRank,
            priceCapUsd, dryRun } and never `regenerate`, so it takes the options
            already on the table. `RebookAgain` below is the other half of the
            pair and keeps re-searching on purpose (§11) — the two now read as
            "take what we found" vs "go find something else".

            Collapsed behind a disclosure, like `RebookAgain`, because the
            console's sections are height-bound (frontend-conventions §18:
            contained scroll, never a page scroll) and an always-open panel would
            squeeze ATTENTION off-screen. What was missing was never the controls
            — it was knowing there is something to take, so the COUNT and the verb
            ride the line that is always visible.

            Hidden by the same pre-run policy gate as every other rebook action:
            with the engine off for this station a run could only end
            POLICY_DISABLED, and the notice above already names the rule. */}
        {towerPolicyGate.disabled ? null : (
          <TowerAutoAccept
            disruptionUrn={disruptionUrn}
            optionsReady={facts ? facts.optionsReady : null}
            loading={liveLoading}
            error={liveError}
            onRetry={() => void refetchLive()}
            running={!!live?.activeRun || !!pending}
          />
        )}
        {/* Re-run the rebooking with other parameters — the honest face of
            "reversible": open groups are recomputed, selected ones stay unless
            cancelled / overridden per PNR. */}
        {/* Stays MOUNTED while its own fire is pending (per-call mutation callbacks
            only run on a mounted observer — unmounting would leave the pending
            marker set forever); it only disables itself. */}
        <RebookAgain
          disruptionUrn={disruptionUrn}
          state={state}
          disabled={card.mode === 'running' || !!pending}
        />
        {/* Departures: "travelled" is read from the airline schedule (mock: the
            rebooked flight's departure time reached) — no push exists for it, so
            the operator can ask for a fresh read while groups wait to depart. */}
        {(card.op === 'none' || card.op === 'contain') && (facts?.ticketedNotDeparted ?? 0) > 0 ? (
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-full gap-1.5 text-xs"
            disabled={checking}
            onClick={() => void checkDepartures()}
            data-testid="tower-check-departures"
            title={t('console.checkDeparturesTitle')}
          >
            <RefreshCw className={cn('h-3.5 w-3.5', checking && 'animate-spin')} aria-hidden />
            {t('console.checkDepartures', {
              travelled: facts?.travelled ?? 0,
              waiting: facts?.ticketedNotDeparted ?? 0,
            })}
          </Button>
        ) : null}
        {supervisorBlocked ? (
          <p className="text-[11px] text-muted-foreground">{t('console.supervisorOnly')}</p>
        ) : null}
        {resolve.isError ? (
          <p className="text-[11px] text-destructive">{t('console.runError')}</p>
        ) : null}
        {fired && fired.startedOp === 'none' && fired.reason ? (
          <p className="text-[11px] text-muted-foreground">{fired.reason}</p>
        ) : null}
        <p className="text-[11px] text-muted-foreground">{card.after}</p>
      </section>

      {/* IN PROGRESS */}
      <section
        className="flex shrink-0 flex-col gap-2 border-b border-border px-4 pb-3 pt-3"
        data-testid="tower-in-progress"
      >
        <PanelHeader
          title={t('console.inProgress.title')}
          badge={run.active && run.pct != null ? `${Math.round(run.pct)} %` : undefined}
          tone="info"
        />
        {run.active ? (
          <div className="grid gap-1.5">
            <div className="flex items-baseline justify-between text-xs">
              <span className="text-muted-foreground">
                {run.title}
                {run.operator ? (
                  <>
                    {' '}
                    · {t('console.inProgress.by')}{' '}
                    <b className="font-semibold text-foreground">{run.operator}</b>
                  </>
                ) : null}
              </span>
              <b className="font-semibold tabular-nums text-foreground">
                {run.total ? `${run.done}/${run.total}` : elapsedLabel(run.startedAt, now, t)}
              </b>
            </div>
            <div
              className="relative h-1.5 overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-valuenow={run.indeterminate ? undefined : Math.round(run.pct ?? 0)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-busy={run.indeterminate ? true : undefined}
            >
              {run.indeterminate ? (
                <div className="tower-indeterminate absolute inset-y-0 w-1/3 rounded-full bg-primary/70" />
              ) : (
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-primary transition-[width] duration-500"
                  style={{ width: `${run.pct ?? 0}%` }}
                />
              )}
            </div>
            {run.note ? <p className="text-[11px] text-muted-foreground">{run.note}</p> : null}
            <div className="flex items-center justify-between gap-2 text-[11.5px] text-muted-foreground">
              <span className="truncate">
                {run.ratePerMin
                  ? `${Math.round(run.ratePerMin)}/min`
                  : elapsedLabel(run.startedAt, now, t)}
                {run.etaMs ? t('console.inProgress.eta', { duration: shortDuration(run.etaMs, t) }) : ''}
                {run.vendors.length ? ` · ${run.vendors.join(' · ')}` : ''}
              </span>
              {run.dialogOp ? (
                <button
                  type="button"
                  className="inline-flex shrink-0 items-center gap-1 font-semibold text-primary hover:underline"
                  onClick={() => setDialogOp(run.dialogOp)}
                >
                  <Activity className="h-3 w-3" aria-hidden /> {t('console.inProgress.watchLive')}
                </button>
              ) : run.link ? (
                <button
                  type="button"
                  className="inline-flex shrink-0 items-center gap-1 font-semibold text-primary hover:underline"
                  onClick={() => run.link && onTarget(run.link.target)}
                  data-testid="tower-in-progress-link"
                >
                  <Activity className="h-3 w-3" aria-hidden /> {run.link.label}
                </button>
              ) : null}
            </div>
            {run.stalledForMs ? (
              <p className="flex items-center gap-1 text-[11.5px] text-amber-700 dark:text-amber-300">
                <TriangleAlert className="h-3 w-3" aria-hidden />{' '}
                {t('console.inProgress.stalled', { duration: shortDuration(run.stalledForMs, t) })}
              </p>
            ) : null}
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span className="truncate">{run.idleLine}</span>
            {run.dialogOp ? (
              <button
                type="button"
                className="inline-flex shrink-0 items-center gap-1 font-semibold text-primary hover:underline"
                onClick={() => setDialogOp(run.dialogOp)}
              >
                <Activity className="h-3 w-3" aria-hidden /> {t('console.inProgress.details')}
              </button>
            ) : null}
          </div>
        )}
      </section>

      {/* ATTENTION */}
      <section
        className="flex min-h-0 flex-1 flex-col gap-2 border-b border-border px-4 pb-3 pt-3"
        data-testid="tower-attention"
      >
        <PanelHeader
          title={t('console.attention.title')}
          badge={attention.length ? String(attention.length) : undefined}
          tone={attention.some((r) => r.tone === 'danger') ? 'danger' : 'warn'}
        />
        <div className="min-h-0 flex-1 overflow-y-auto pr-0.5">
          {attention.length === 0 ? (
            <p
              className="flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
              data-testid="tower-attention-empty"
            >
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> {t('console.attention.empty')}
            </p>
          ) : (
            <ul className="grid gap-1">
              {attention.map((row) => (
                <AttentionItem
                  key={row.key}
                  row={row}
                  onOpen={() =>
                    row.action === 'repair-issue'
                      ? repairIssue(row.n)
                      : row.action === 'send-offers'
                        ? sendOffers(row.n)
                        : row.action === 'redispatch-transfers'
                          ? redispatchNoShows(row.n)
                          : row.primary
                            ? row.op && row.op !== card.op
                              ? fire(row.op)
                              : onPrimary()
                            : row.target
                              ? onTarget(row.target)
                              : undefined
                  }
                />
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* LOG */}
      <section className="shrink-0 bg-muted/40 px-4 py-2" data-testid="tower-log">
        <div className="flex items-center justify-between">
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
            {t('console.log.title')}
          </span>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-primary hover:underline"
            onClick={() => void copySummary()}
          >
            {copied ? (
              <Check className="h-3 w-3" aria-hidden />
            ) : (
              <ClipboardCopy className="h-3 w-3" aria-hidden />
            )}{' '}
            {t('console.log.copy')}
          </button>
        </div>
        <ul className="mt-1 grid max-h-[84px] gap-0.5 overflow-hidden text-[11px]">
          {lines.slice(0, 4).map((l, i) => (
            <li key={`${l.at}-${i}`} className="grid grid-cols-[38px_1fr] gap-2">
              <span className="font-mono text-muted-foreground">
                {new Date(l.at).toLocaleTimeString(undefined, {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              <span
                className={cn(
                  'truncate',
                  l.tone === 'danger' && 'text-destructive',
                  l.tone === 'warn' && 'text-amber-700 dark:text-amber-300',
                )}
              >
                {l.text}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <style>{`.tower-indeterminate{animation:tower-indeterminate 1.4s ease-in-out infinite}@keyframes tower-indeterminate{0%{left:-33%}100%{left:100%}}@media (prefers-reduced-motion: reduce){.tower-indeterminate{animation:none;left:0;width:100%}}`}</style>
      <TowerConfirmDialog
        op={confirmOp}
        state={state}
        isFiring={resolve.isPending}
        onConfirm={() => confirmOp && fire(confirmOp)}
        onCancel={() => setConfirmOp(null)}
      />
      {dialogOp ? (
        <RebookingProgressDialog
          disruptionUrn={disruptionUrn}
          open={!!dialogOp}
          onOpenChange={(open) => (open ? undefined : setDialogOp(null))}
          op={dialogOp}
        />
      ) : null}
    </aside>
  );
}

/**
 * TAKE THE RECOMMENDATION — the Tower's bulk-accept (auto-accept-rebooking / U2).
 *
 * A thin, stateful frame around the REUSED `AutomaticAcceptancePanel`; it owns no
 * seam of its own. Its whole job is to answer, before the operator opens
 * anything, "is there a recommendation waiting, and how many" — which is the
 * question the Tower could not answer at all.
 *
 * Four UX states, every one of them read off the WS-backed progress cache —
 * NO POLLING anywhere in this file's data path. The count moves because
 * `applyEvent` writes each disruption-room push into `['disruptions', urn,
 * 'progress']` (setQueryData, staleTime Infinity, no refetch interval of any
 * kind), and `liveFacts` folds that snapshot over the resolution-state facts
 * into `optionsReady`. The one timer in this module is the console's wall clock
 * for elapsed/ETA — a clock, not a fetch.
 *
 *   loading · the seed read is still in flight — the count is not known yet, so
 *             a skeleton, never a button over an unknown number
 *   error   · the seed read failed — name it and offer a retry
 *   empty   · 0 groups hold an untaken recommendation — SAY SO. This is the state
 *             the brief calls out: a dead button here is worse than no button
 *   success · N > 0 (or unknown-but-not-zero) — the count, the two facts an
 *             operator must hold before clicking, and the panel
 */
function TowerAutoAccept({
  disruptionUrn,
  optionsReady,
  loading,
  error,
  onRetry,
  running,
}: {
  disruptionUrn: string;
  /**
   * Groups at OPTIONS_READY: an option was computed and nobody took it.
   * `null` = the BFF could not tell us (`kpis.groups.known.cases` false). That
   * DEGRADES OPEN — unknown is not zero, so the affordance stays and only the
   * count is withheld. Withholding the action on an unreadable aggregate is how
   * the Tower lost it in the first place.
   */
  optionsReady: number | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  /**
   * A run holds the disruption-scoped lock, so a LIVE accept would come back 409
   * (the panel catches it and names the holder). Said once, up front, rather than
   * unmounting the whole affordance: a dry-run books nothing, takes no lock and
   * stays genuinely useful while a run settles.
   */
  running: boolean;
}) {
  const { t } = useTranslation('tower');
  const [open, setOpen] = useState(false);
  // Once opened the panel stays MOUNTED and is only hidden, never unmounted.
  // Two reasons, both learned elsewhere in this file: a per-call mutation
  // observer that unmounts mid-flight never runs its callbacks (the same trap
  // `RebookAgain` is kept mounted for), so collapsing during a SIMULATE would
  // silently swallow the preview; and the rank / USD cap the operator typed
  // would reset every time they collapsed to glance at ATTENTION. Mounting is
  // free — the panel fires no read of its own, only a mutation on click.
  const [everOpened, setEverOpened] = useState(false);

  if (loading) {
    return (
      <div className="grid gap-1.5" data-testid="tower-auto-accept-loading" aria-busy="true">
        <Skeleton className="h-3 w-40" />
        <Skeleton className="h-3 w-28" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex flex-wrap items-center justify-between gap-2 text-[11px]"
        data-testid="tower-auto-accept-error"
      >
        <span className="text-muted-foreground">{t('autoAccept.error')}</span>
        <button
          type="button"
          onClick={onRetry}
          className="font-semibold text-primary hover:underline"
          data-testid="tower-auto-accept-retry"
        >
          {t('autoAccept.retry')}
        </button>
      </div>
    );
  }

  // EMPTY — nothing is holding an untaken recommendation. One muted line that
  // says which of the two reasons it is; never a button that would run over
  // zero groups and report success at having done nothing.
  if (optionsReady === 0) {
    return (
      <p className="text-[11px] text-muted-foreground" data-testid="tower-auto-accept-empty">
        {t('autoAccept.empty')}
      </p>
    );
  }

  return (
    <div className="grid gap-1.5" data-testid="tower-auto-accept">
      <p
        className="text-[11.5px] font-medium leading-snug text-foreground"
        data-testid="tower-auto-accept-lead"
      >
        {optionsReady == null
          ? t('autoAccept.leadUnknown')
          : t('autoAccept.lead', { count: optionsReady })}
      </p>
      <button
        type="button"
        onClick={() => {
          setEverOpened(true);
          setOpen((v) => !v);
        }}
        className="inline-flex items-center gap-1 self-start text-[11px] font-semibold text-primary hover:underline"
        aria-expanded={open}
        data-testid="tower-auto-accept-toggle"
      >
        {open ? (
          <ChevronDown className="h-3 w-3" aria-hidden />
        ) : (
          <ChevronRight className="h-3 w-3" aria-hidden />
        )}
        <Sparkles className="h-3 w-3" aria-hidden /> {t('autoAccept.toggle')}
      </button>
      {everOpened ? (
        // The `hidden` utility sits on a BARE wrapper, never on the `grid` box
        // below: two display utilities on one element resolve by stylesheet
        // order, and "collapsed" silently rendering open is not a coin toss
        // worth taking.
        <div className={cn(!open && 'hidden')} data-testid="tower-auto-accept-collapsible">
          <div
            className="grid gap-2 rounded-md border border-border bg-muted/30 p-2.5"
            data-testid="tower-auto-accept-panel"
          >
            {/* The two things the operator must hold BEFORE the panel's own
                button: this takes what is already computed (no fresh vendor
                search), and it HOLDS a seat — it does not issue a ticket. Both
                are invariants of the slice, so they are written here rather
                than left to be inferred. */}
            <p className="text-[11px] leading-snug text-muted-foreground">
              {t('autoAccept.noRegenerate')}
            </p>
            <p className="text-[11px] leading-snug text-muted-foreground">
              {t('autoAccept.holds')}
            </p>
            {running ? (
              <p
                className="text-[11px] leading-snug text-amber-700 dark:text-amber-300"
                role="status"
                data-testid="tower-auto-accept-running"
              >
                {t('autoAccept.running')}
              </p>
            ) : null}
            {/* REUSED VERBATIM from the classic action sidebar — same
                component, same seam, same copy (`disruptions:autoAccept.*`). It
                carries the quality rank, the USD cap and DRY-RUN (default ON),
                which is what keeps invariant §6.3 reachable: auto-accepting
                takes real seat-hold inventory, so the operator can price and
                size it before committing. */}
            <AutomaticAcceptancePanel disruptionUrn={disruptionUrn} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PanelHeader({
  title,
  badge,
  tone,
}: {
  title: string;
  badge?: string | undefined;
  tone: 'ok' | 'warn' | 'danger' | 'info';
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
        {title}
      </span>
      {badge ? (
        <span
          className={cn(
            'rounded-full px-1.5 py-px text-[10px] font-semibold',
            tone === 'ok' &&
              'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
            tone === 'warn' &&
              'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
            tone === 'danger' && 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300',
            tone === 'info' && 'bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-300',
          )}
        >
          {badge}
        </span>
      ) : null}
    </div>
  );
}

function AttentionItem({ row, onOpen }: { row: AttentionRow; onOpen: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="grid w-full grid-cols-[40px_1fr_auto] items-center gap-2 rounded-md border border-border px-2 py-1.5 text-left text-xs hover:bg-muted/50"
        data-testid={`tower-attention-${row.key}`}
      >
        <span
          className={cn(
            'text-right text-base font-bold tabular-nums',
            row.tone === 'warn' && 'text-amber-700 dark:text-amber-300',
            row.tone === 'danger' && 'text-rose-700 dark:text-rose-300',
            row.tone === 'info' && 'text-sky-700 dark:text-sky-300',
          )}
        >
          {row.n}
        </span>
        <span className="min-w-0 text-foreground">
          <b className="font-semibold">{row.label}</b>
          {row.detail ? <span className="text-muted-foreground"> · {row.detail}</span> : null}
        </span>
        <span className="inline-flex items-center gap-0.5 whitespace-nowrap text-[11px] font-semibold text-primary">
          {row.verb} <ChevronRight className="h-3 w-3" aria-hidden />
        </span>
      </button>
    </li>
  );
}

/** ONE confirm regime for the COMMIT ops — same tally, same facts as the classic hero. */
function TowerConfirmDialog({
  op,
  state,
  isFiring,
  onConfirm,
  onCancel,
}: {
  op: ResolutionOp | null;
  state: ResolutionState;
  isFiring: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('tower');
  const open = !!op;
  const g = state.kpis.groups;
  const cases = state.kpis.caseCount;
  const heldPriceMinor = state.kpis.heldPriceMinor;
  const currency = state.kpis.currency;
  const step = state.steps.find((s) => s.op === op);
  const n = step?.remaining ?? step?.total ?? 0;
  const forced = op === 'contain' && step?.containMode !== 'NATURAL';
  const title =
    op === 'tickets'
      ? t('console.confirm.ticketsTitle', {
          count: n,
          flights: t('count.flights', { count: cases }),
        })
      : op === 'acceptance'
        ? t('console.confirm.acceptanceTitle', { count: n })
        : forced
          ? t('console.confirm.containForcedTitle')
          : t('console.confirm.containTitle');
  const body =
    op === 'tickets'
      ? t('console.confirm.ticketsBody')
      : op === 'acceptance'
        ? t('console.confirm.acceptanceBody', {
            held: formatPriceMinor(heldPriceMinor, currency, t),
          })
        : forced
          ? t('console.confirm.containForcedBody', {
              holds: g?.hotelHeld ?? 0,
              selections: g?.assigned ?? 0,
            })
          : t('console.confirm.containBody');
  const residual = residualsLine(state, t);
  const button =
    op === 'tickets'
      ? t('console.confirm.buttonIssue', { count: n })
      : op === 'acceptance'
        ? t('console.confirm.buttonForceAcceptance', { count: n })
        : forced
          ? t('console.confirm.buttonForceContain')
          : t('console.confirm.buttonContain');
  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onCancel())}>
      <DialogContent data-testid="tower-confirm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>
        {g ? (
          <div className="rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
            {t('console.confirm.facts', {
              assigned: g.assigned,
              rebookable: g.rebookable,
              held: g.hotelHeld,
              confirmed: g.hotelConfirmed,
              accepted: g.accepted,
              offerReady: Math.max(g.offerReady ?? 0, g.atGate),
              issued: g.issued ?? g.ticketed,
            })}
            {residual ? (
              <div className="mt-1 text-amber-700 dark:text-amber-300">
                {t('console.confirm.leftOut', { list: residual })}
              </div>
            ) : null}
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isFiring}>
            {t('common:common.cancel')}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isFiring}
            className={cn(forced && 'bg-rose-700 hover:bg-rose-800')}
          >
            {isFiring ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Play className="mr-1.5 h-4 w-4" aria-hidden />
            )}
            {button}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function residualsLine(state: ResolutionState, t: TowerT): string {
  const p = state.problems;
  const parts: string[] = [];
  const noOptions = Object.values(p.noOptionsByReason ?? {}).reduce((a, b) => a + b, 0);
  if (noOptions) parts.push(t('console.residual.noOptions', { count: noOptions }));
  if (p.allocationGaps) parts.push(t('console.residual.gaps', { count: p.allocationGaps }));
  if (p.declined) parts.push(t('console.residual.declined', { count: p.declined }));
  if (p.commitFailed) parts.push(t('console.residual.commitFailed', { count: p.commitFailed }));
  return parts.join(' · ');
}

export type { NowCard };
